import React, { useMemo } from "react";
import { cn } from "../../utils";

interface TextHighlightProps {
  text: string;
  keyword: string | null | undefined;
  className?: string;
  highlightClassName?: string;
}

/**
 * TextHighlight Component
 *
 * Highlights multiple keywords within a text string.
 * Supports space-separated keywords (e.g., "apple banana" highlights both "apple" and "banana").
 * Case-insensitive matching.
 */
export const TextHighlight: React.FC<TextHighlightProps> = ({
  text,
  keyword,
  className,
  highlightClassName = "text-primary-text bg-primary/5 font-semibold px-0.5 rounded",
}) => {
  const parts = useMemo(() => {
    if (!keyword || !text) return [{ text: text || "", isHighlight: false }];

    // 1. Split keyword into tokens and filter empty strings
    const tokens = keyword.split(/\s+/).filter((k) => k.trim().length > 0);

    if (tokens.length === 0) return [{ text, isHighlight: false }];

    // 2. Escape special regex characters in tokens
    const escapedTokens = tokens.map((t) =>
      t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    );

    // 3. Create a RegExp to match any of the tokens (case-insensitive)
    const regex = new RegExp(`(${escapedTokens.join("|")})`, "gi");

    // 4. Split text by regex, keeping the delimiters (matched keywords)
    return text.split(regex).map((part) => {
      // Check if this part matches any of the tokens (case-insensitive)
      const isMatch = tokens.some(
        (token) => part.toLowerCase() === token.toLowerCase(),
      );
      return { text: part, isHighlight: isMatch };
    });
  }, [text, keyword]);

  return (
    <span className={className}>
      {parts.map((part, index) =>
        part.isHighlight ? (
          <span key={index} className={cn(highlightClassName)}>
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </span>
  );
};
